import { A4_METRICS } from "@/api/document/paginate";

export interface FloatImage {
	id: number;
	file: string;
	anchorId: string | null;
	pageId: string | null;
	x: number;
	y: number;
	width: number;
	height: number;
}

export type FloatDraft = Omit<FloatImage, "id">;

export interface FloatPage {
	id: string | null | undefined;
	blocks: string[];
}

export const FloatingPlacement = {
	fitToFlow(
		margins: { top: number; right: number; bottom: number; left: number },
		source: { width: number; height: number },
	) {
		const flowWidth = A4_METRICS.pageWidth - margins.left - margins.right;
		const flowHeight = A4_METRICS.pageHeight - margins.top - margins.bottom;
		const scale = Math.min(1, flowWidth / source.width, flowHeight / source.height);

		return {
			width: Math.max(1, Math.round(source.width * scale)),
			height: Math.max(1, Math.round(source.height * scale)),
		};
	},

	resolve(pages: FloatPage[], floats: FloatImage[]): FloatImage[][] {
		const paints: FloatImage[][] = pages.map(() => []);

		if (pages.length === 0) {
			return paints;
		}

		const indexOfPage = new Map<string, number>();
		const pageOfBlock = new Map<string, number>();

		for (const [pageIndex, page] of pages.entries()) {
			if (typeof page.id === "string") {
				indexOfPage.set(page.id, pageIndex);
			}

			for (const blockId of page.blocks) {
				pageOfBlock.set(blockId, pageIndex);
			}
		}

		for (const float of floats) {
			if (float.pageId !== null) {
				const index = indexOfPage.get(float.pageId);

				if (index !== undefined) {
					paints[index].push(float);
				}

				continue;
			}

			if (float.anchorId !== null) {
				paints[pageOfBlock.get(float.anchorId) ?? 0].push(float);

				continue;
			}

			for (const paint of paints) {
				paint.push(float);
			}
		}

		return paints;
	},

	resolveFlow(groups: string[][], pageIds: string[], floats: FloatImage[]) {
		const pages = groups.map((blocks, index) => ({ id: pageIds.at(index), blocks }));
		const paints = FloatingPlacement.resolve(pages, floats);
		const sourcePageId = pageIds.at(-1);

		if (sourcePageId === undefined || groups.length <= pageIds.length) {
			return paints;
		}

		const furniture = floats.filter((float) => float.pageId === sourcePageId);

		for (let index = pageIds.length; index < paints.length; index += 1) {
			paints[index].push(...furniture);
		}

		return paints;
	},

	reanchor(previousOrder: string[], survivingOrder: string[], anchorId: string): string | null {
		const surviving = new Set(survivingOrder);

		if (surviving.has(anchorId)) {
			return anchorId;
		}

		const origin = previousOrder.indexOf(anchorId);

		if (origin === -1) {
			return null;
		}

		for (let distance = 1; distance < previousOrder.length; distance += 1) {
			const beforeIndex = origin - distance;

			if (beforeIndex >= 0 && surviving.has(previousOrder[beforeIndex])) {
				return previousOrder[beforeIndex];
			}

			const afterIndex = origin + distance;

			if (afterIndex < previousOrder.length && surviving.has(previousOrder[afterIndex])) {
				return previousOrder[afterIndex];
			}
		}

		return null;
	},
};
